import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, History, FileSpreadsheet, Download, Undo2 } from 'lucide-react';
import { api, fmt, type System } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmModal from './../components/ConfirmModal';
import { useToast } from '../contexts/ToastContext';

export default function Payments() {
  const { sys: sysSlug } = useParams<{ sys: string }>();
  const sys = sysSlug === 'messer' ? 'Messer' : sysSlug === 'empire-aca' ? 'Empire-ACA' : null;
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['payments', sys],
    queryFn: () => api.listPayments(sys as System),
    enabled: !!sys,
  });

  if (!sys) return <div className="alert alert-warning"><p>Pick a system.</p></div>;
  if (q.isLoading) return <LoadingSpinner label="Loading payment history…" />;

  const payments = q.data?.payments ?? [];
  const total = payments.reduce((s, p) => s + (p.total || 0), 0);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-start gap-3 justify-between flex-wrap">
        <div className="flex items-center gap-3">
          <Link
            to={`/${sysSlug}/pay`}
            className="btn-icon"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
              <History
                size={20}
                className="inline mr-2"
                style={{ color: 'var(--color-primary)' }}
              />
              Payment history — {sys}
            </h2>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              {payments.length} payments · {fmt.money(total)} total paid
            </p>
          </div>
        </div>
        <CsvExportControls sys={sys} />
      </div>

      {payments.length === 0 ? (
        <div className="card text-center py-12">
          <p style={{ color: 'var(--color-text-muted)' }}>No payments yet.</p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="table">
            <thead>
              <tr>
                <th>Paid at</th>
                <th>Agent</th>
                <th className="text-right">Rows</th>
                <th>Carriers</th>
                <th className="text-right">Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr
                  key={p.payment_id}
                  onClick={() => navigate(`/${sysSlug}/payments/${p.payment_id}`)}
                  className="cursor-pointer"
                >
                  <td className="text-sm">{fmt.date(p.paid_at)}</td>
                  <td className="font-medium">{p.agent_name}</td>
                  <td className="text-right font-mono">{p.row_count}</td>
                  <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {Object.entries(p.by_carrier || {})
                      .map(([c, v]) => `${c} (${v.rows})`)
                      .join(', ')}
                  </td>
                  <td className="text-right font-mono font-semibold">{fmt.money(p.total)}</td>
                  <td className="text-right text-sm" style={{ color: 'var(--color-primary)' }}>
                    View →
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CsvExportControls({ sys }: { sys: System }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const toast = useToast();
  return (
    <div className="card p-3 flex items-end gap-2 flex-wrap" style={{ marginTop: 0 }}>
      <div>
        <label className="label" style={{ marginBottom: '2px', fontSize: '0.7rem' }}>
          From
        </label>
        <input
          type="date"
          className="input text-sm"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          style={{ minWidth: '130px' }}
        />
      </div>
      <div>
        <label className="label" style={{ marginBottom: '2px', fontSize: '0.7rem' }}>
          To
        </label>
        <input
          type="date"
          className="input text-sm"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          style={{ minWidth: '130px' }}
        />
      </div>
      <button
        onClick={() => {
          api
            .downloadPaymentsCsv(sys, from || undefined, to || undefined)
            .catch((err) => toast.error(`CSV download failed: ${(err as Error).message}`));
        }}
        className="btn btn-success text-sm flex items-center gap-2"
      >
        <Download size={14} /> Download CSV
      </button>
    </div>
  );
}

export function PaymentDetail() {
  const { sys: sysSlug, paymentId } = useParams<{ sys: string; paymentId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmReverse, setConfirmReverse] = useState(false);
  const [reversing, setReversing] = useState(false);

  const q = useQuery({
    queryKey: ['payment', paymentId],
    queryFn: () => api.getPayment(paymentId!),
    enabled: !!paymentId,
  });

  if (q.isLoading) return <LoadingSpinner label="Loading payment…" />;
  if (q.isError) return <div className="alert alert-error"><p>{(q.error as Error).message}</p></div>;

  const data = q.data!;

  const handleReverse = async () => {
    setReversing(true);
    try {
      const r = await api.reversePayment(paymentId!);
      toast.success(`Reversed payment. ${r.rows_flipped} rows flipped back to unpaid.`);
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['pay-summary'] });
      queryClient.invalidateQueries({ queryKey: ['pay-runs'] });
      navigate(`/${sysSlug}/payments`, { replace: true });
    } catch (err) {
      toast.error(`Reverse failed: ${(err as Error).message}`);
      setReversing(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-start gap-3 justify-between flex-wrap">
        <div className="flex items-center gap-3">
          <Link
            to={`/${sysSlug}/payments`}
            className="btn-icon"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
              Payment to {data.payment.agent_name}
            </h2>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              {fmt.date(data.payment.paid_at)} ·{' '}
              <span className="font-mono">{data.payment.payment_id}</span>
            </p>
          </div>
        </div>
        <button
          onClick={() => setConfirmReverse(true)}
          className="btn btn-danger text-sm flex items-center gap-2"
        >
          <Undo2 size={14} /> Reverse payment
        </button>
      </div>

      <ConfirmModal
        open={confirmReverse}
        title={`Reverse payment to ${data.payment.agent_name}?`}
        message={`Flips ${data.payment.row_count} rows back to unpaid (${fmt.money(data.payment.total)}) and removes this Payment record. The rows show up in Pay again. NOTE: this only updates the system — if money was actually sent, you need to recover it separately.`}
        confirmLabel={reversing ? 'Reversing…' : 'Reverse payment'}
        variant="danger"
        onConfirm={handleReverse}
        onCancel={() => setConfirmReverse(false)}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="metric-card metric-card-success">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}>
            Total paid
          </p>
          <p className="text-2xl font-bold font-mono" style={{ color: 'var(--color-text)' }}>
            {fmt.money(data.payment.total)}
          </p>
        </div>
        <div className="metric-card metric-card-primary">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}>
            Rows
          </p>
          <p className="text-2xl font-bold font-mono" style={{ color: 'var(--color-text)' }}>
            {data.payment.row_count}
          </p>
        </div>
        <div className="metric-card metric-card-primary">
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}>
            Carriers
          </p>
          <p className="text-2xl font-bold font-mono" style={{ color: 'var(--color-text)' }}>
            {Object.keys(data.payment.by_carrier || {}).length}
          </p>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            <FileSpreadsheet size={14} className="inline mr-2" />
            Rows in this payment ({data.rows.length})
          </h3>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Carrier</th>
              <th>Policy</th>
              <th>Policyholder</th>
              <th className="text-right">Raw</th>
              <th className="text-right">Override</th>
              <th className="text-right">Net</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={`${r.batch_id}-${r.row_idx}`}>
                <td className="text-sm">{r.carrier}</td>
                <td className="font-mono text-xs">{r.policy_id}</td>
                <td>{r.policyholder_name}</td>
                <td className="text-right font-mono">{fmt.money(r.raw_amount)}</td>
                <td
                  className="text-right font-mono text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                  title={r.override_formula}
                >
                  {fmt.money(r.override_amount)}
                </td>
                <td className="text-right font-mono font-semibold">{fmt.money(r.net_amount)}</td>
                <td className="text-xs">{fmt.dateOnly(r.transaction_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
